import assert from 'assert';
import {produce} from 'immer';
import {v4 as uuidv4} from 'uuid';
import {assign, setup, assertEvent} from 'xstate';

import {
  PLATE_MAP_MAX_CONCENTRATION_LAYERS,
  PatternDirection,
  PlateMap,
} from 'samples/plate-maps/PlateMapConstants';
import {calculateConcentrationPreviewPlateMap} from 'samples/plate-maps/PlateMapUtil';
import {HydratedUnit} from 'units/util';

/**
 * Dilution settings for a concentration layer
 */
export interface DilutionSettings {
  /** Dilution factor (e.g., 2 for 1:2 dilution) */
  dilutionFactor: number | null;
  /** Direction to apply dilutions */
  dilutionDirection: PatternDirection;
  /** Number of replicates */
  replicates: number | null;
  /** Direction to apply replicates */
  replicateDirection: PatternDirection;
}

/**
 * Represents a single concentration layer's data
 */
export interface ConcentrationLayerData {
  /** Unique identifier for this concentration layer */
  layerId: string;
  /** Name of the concentration layer */
  name: string;
  /** Concentration value */
  concentration: number | null;
  /** Concentration unit */
  concentrationUnit: HydratedUnit | null;
  /** Whether dilution settings are enabled for this layer */
  allowSetDilutionSettings: boolean;
  /** Dilution settings for this layer */
  dilutionSettings: DilutionSettings;
}

/**
 * Events for the ConcentrationLayerMachine
 */
export type ConcentrationLayerEvents =
  | {type: 'addLayer'; name?: string}
  | {type: 'removeLayer'; layerId: string}
  | {type: 'setActiveLayer'; layerId: string | null}
  | {type: 'updateLayerName'; layerId: string; name: string}
  | {
      type: 'updateLayerConcentration';
      layerId: string;
      concentration: number | null;
    }
  | {type: 'updateLayerUnit'; layerId: string; unit: HydratedUnit | null}
  | {
      type: 'updateLayerAllowSetDilutionSettings';
      layerId: string;
      allowSetDilutionSettings: boolean;
    }
  | {
      type: 'updateLayerDilutionSettings';
      layerId: string;
      settings: Partial<DilutionSettings>;
    }
  | {type: 'updateSelectedWellIndices'; selectedWellIndices: Set<number>}
  | {type: 'updatePlateMap'; plateMap: PlateMap};

/**
 * Context for the ConcentrationLayerMachine - now manages multiple layers
 */
export interface ConcentrationLayerContext {
  /** Map of layer ID to layer data */
  layers: Map<string, ConcentrationLayerData>;
  /** Ordered list of layer IDs for display purposes */
  orderedLayerIds: Array<string>;
  /** Currently active/selected layer for editing */
  activeLayerId: string | null;
  /** Set of well indices that are selected for this layer */
  selectedWellIndices: Set<number>;
  /** Current plate map */
  plateMap: PlateMap | null;
  /** Plate map with concentration preview values */
  previewPlateMap: PlateMap | null;
}

/**
 * Input for creating a ConcentrationLayerMachine
 */
export interface ConcentrationLayerMachineInput {
  /** Initial layers to create */
  initialLayers?: Array<ConcentrationLayerData>;
  /** Initial plate map */
  initialPlateMap?: PlateMap;
}

export function getDefaultDilutionSettings(): DilutionSettings {
  return {
    dilutionFactor: null,
    dilutionDirection: PatternDirection.RIGHT,
    replicates: null,
    replicateDirection: PatternDirection.RIGHT,
  };
}

/**
 * Helper function to create a new layer with default settings
 */
function createDefaultLayerSettings(layerId: string, name: string): ConcentrationLayerData {
  const dilutionSettings: DilutionSettings = getDefaultDilutionSettings();

  return {
    layerId,
    name,
    concentration: null,
    concentrationUnit: null,
    allowSetDilutionSettings: false,
    dilutionSettings,
  };
}

function createConcentrationLayerId(): string {
  return `concentration-layer-${uuidv4()}`;
}

/**
 * State machine for managing multiple concentration layers
 */
export const ConcentrationLayerMachine = setup({
  types: {
    input: {} as ConcentrationLayerMachineInput,
    context: {} as ConcentrationLayerContext,
    events: {} as ConcentrationLayerEvents,
  },
  actions: {
    /**
     * Add a new concentration layer to the machine
     *
     * This action will create a new layer with default settings and add it to the machine.
     */
    addLayer: assign(({context, event}) => {
      assertEvent(event, 'addLayer');
      assert(
        context.layers.size < PLATE_MAP_MAX_CONCENTRATION_LAYERS,
        `Cannot add concentration layer. Maximum allowed is ${PLATE_MAP_MAX_CONCENTRATION_LAYERS}.`
      );

      const layerId = createConcentrationLayerId();
      const layerName = event.name || `Concentration Layer ${context.layers.size + 1}`;
      const newLayer = createDefaultLayerSettings(layerId, layerName);

      return produce(context, (draft) => {
        draft.layers.set(layerId, newLayer);
        draft.orderedLayerIds.push(layerId);
      });
    }),

    /**
     * Remove a concentration layer from the machine
     *
     * This action will remove the layer from the machine. If the layer being removed is the active layer,
     * the active layer will be set to the first layer in the ordered list.
     */
    removeLayer: assign(({context, event}) => {
      assertEvent(event, 'removeLayer');

      const layer = context.layers.get(event.layerId);
      if (!layer) {
        return {};
      }

      return produce(context, (draft) => {
        draft.layers.delete(event.layerId);
        draft.orderedLayerIds = draft.orderedLayerIds.filter((id) => id !== event.layerId);

        if (context.activeLayerId === event.layerId) {
          draft.activeLayerId = draft.orderedLayerIds.length > 0 ? draft.orderedLayerIds[0] : null;
        }
      });
    }),

    /**
     * Set the active layer for editing
     *
     * This is the layer that is currently selected and will be used to calculate the preview plate map.
     */
    setActiveLayer: assign(({context, event}) => {
      assertEvent(event, 'setActiveLayer');

      if (event.layerId != null && !context.layers.has(event.layerId)) {
        return {};
      }

      return produce(context, (draft) => {
        draft.activeLayerId = event.layerId;
        draft.previewPlateMap = calculateConcentrationPreviewPlateMap(draft);
      });
    }),

    /**
     * Update the name of a concentration layer
     */
    updateLayerName: assign(({context, event}) => {
      assertEvent(event, 'updateLayerName');

      const layer = context.layers.get(event.layerId);
      if (!layer) {
        return {};
      }

      return produce(context, (draft) => {
        const draftLayer = draft.layers.get(event.layerId);
        if (draftLayer) {
          draftLayer.name = event.name;
        }
      });
    }),

    /**
     * Update the concentration of a concentration layer
     *
     * This action will update the concentration of the layer and recalculate the preview plate map if the layer is
     * the active layer.
     */
    updateLayerConcentration: assign(({context, event}) => {
      assertEvent(event, 'updateLayerConcentration');

      const layer = context.layers.get(event.layerId);
      if (!layer) {
        return {};
      }

      return produce(context, (draft) => {
        const draftLayer = draft.layers.get(event.layerId);
        if (draftLayer) {
          draftLayer.concentration = event.concentration;
          if (event.layerId === draft.activeLayerId) {
            draft.previewPlateMap = calculateConcentrationPreviewPlateMap(draft);
          }
        }
      });
    }),

    /**
     * Update the unit of a concentration layer
     *
     * This action will update the unit of the layer and recalculate the preview plate map if the layer is
     * the active layer.
     */
    updateLayerUnit: assign(({context, event}) => {
      assertEvent(event, 'updateLayerUnit');

      const layer = context.layers.get(event.layerId);
      if (!layer) {
        return {};
      }

      return produce(context, (draft) => {
        const draftLayer = draft.layers.get(event.layerId);
        if (draftLayer) {
          draftLayer.concentrationUnit = event.unit;
          if (event.layerId === draft.activeLayerId) {
            draft.previewPlateMap = calculateConcentrationPreviewPlateMap(draft);
          }
        }
      });
    }),

    /**
     * Update the allow set dilution settings of a concentration layer
     *
     * This action will update the allow set dilution settings of the layer and recalculate the preview plate map
     * if the layer is the active layer.
     */
    updateLayerAllowSetDilutionSettings: assign(({context, event}) => {
      assertEvent(event, 'updateLayerAllowSetDilutionSettings');

      const layer = context.layers.get(event.layerId);
      if (!layer) {
        return {};
      }

      return produce(context, (draft) => {
        const draftLayer = draft.layers.get(event.layerId);
        if (draftLayer) {
          draftLayer.allowSetDilutionSettings = event.allowSetDilutionSettings;
        }
        if (event.layerId === draft.activeLayerId) {
          draft.previewPlateMap = calculateConcentrationPreviewPlateMap(draft);
        }
      });
    }),

    /**
     * Update the dilution settings of a concentration layer
     *
     * This action will update the dilution settings of the layer. If the allow set dilution settings is false,
     * the dilution settings will not be changed. If the layer is the active layer, the preview plate map will be
     * recalculated.
     */
    updateLayerDilutionSettings: assign(({context, event}) => {
      assertEvent(event, 'updateLayerDilutionSettings');

      const layer = context.layers.get(event.layerId);
      if (!layer || !layer.allowSetDilutionSettings) {
        return {};
      }

      return produce(context, (draft) => {
        const draftLayer = draft.layers.get(event.layerId);
        if (draftLayer) {
          Object.assign(draftLayer.dilutionSettings, event.settings);
          if (event.layerId === draft.activeLayerId) {
            draft.previewPlateMap = calculateConcentrationPreviewPlateMap(draft);
          }
        }
      });
    }),

    /**
     * Update the selected well indices
     *
     * This action will update the selected well indices and recalculate the preview plate map.
     */
    updateSelectedWellIndices: assign(({context, event}) => {
      assertEvent(event, 'updateSelectedWellIndices');

      return produce(context, (draft) => {
        draft.selectedWellIndices = event.selectedWellIndices;
        draft.previewPlateMap = calculateConcentrationPreviewPlateMap(draft);
      });
    }),

    /**
     * Update the plate map
     *
     * This action will update the plate map and recalculate the preview plate map.
     */
    updatePlateMap: assign(({context, event}) => {
      assertEvent(event, 'updatePlateMap');

      return produce(context, (draft) => {
        draft.plateMap = event.plateMap;
        draft.previewPlateMap = calculateConcentrationPreviewPlateMap(draft);
      });
    }),
  },
}).createMachine({
  context: ({input}) => {
    const layers = new Map<string, ConcentrationLayerData>();
    const orderedLayerIds: Array<string> = [];

    // Initialize with any provided initial layers
    if (input.initialLayers) {
      assert(
        input.initialLayers.length <= PLATE_MAP_MAX_CONCENTRATION_LAYERS,
        `Cannot initialize ConcentrationLayerMachine with ${input.initialLayers.length} layers. ` +
          `Maximum allowed is ${PLATE_MAP_MAX_CONCENTRATION_LAYERS}.`
      );

      for (const layerData of input.initialLayers) {
        layers.set(layerData.layerId, layerData);
        orderedLayerIds.push(layerData.layerId);
      }
    }

    const initialContext = {
      layers,
      orderedLayerIds,
      activeLayerId: orderedLayerIds.length > 0 ? orderedLayerIds[0] : null,
      selectedWellIndices: new Set<number>(),
      plateMap: input.initialPlateMap ?? null,
      previewPlateMap: null,
    };

    return initialContext;
  },

  id: 'ConcentrationLayer',
  initial: 'idle',

  states: {
    idle: {
      on: {
        addLayer: {
          actions: 'addLayer',
        },
        removeLayer: {
          actions: 'removeLayer',
        },
        setActiveLayer: {
          actions: 'setActiveLayer',
        },
        updateLayerName: {
          actions: 'updateLayerName',
        },
        updateLayerConcentration: {
          actions: 'updateLayerConcentration',
        },
        updateLayerUnit: {
          actions: 'updateLayerUnit',
        },
        updateLayerAllowSetDilutionSettings: {
          actions: 'updateLayerAllowSetDilutionSettings',
        },
        updateLayerDilutionSettings: {
          actions: 'updateLayerDilutionSettings',
        },
        updateSelectedWellIndices: {
          actions: 'updateSelectedWellIndices',
        },
        updatePlateMap: {
          actions: 'updatePlateMap',
        },
      },
    },
  },
});
